import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { Modal, Button, Spinner } from '../../../../UI'
import { getCompanyDetails, deleteCompany, updateCompany } from '../../../../store/actions'
import { FiEdit2, FiTrash2, FiX } from 'react-icons/fi'
import Container from './styles'

const CompanyDetails = () => {
  const { companyId } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [company, setCompany] = useState(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('overview')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    if (companyId) {
      setLoading(true)
      dispatch(getCompanyDetails(companyId))
        .then((response) => {
          if (response && response.data) {
            setCompany(response.data)
          }
          setLoading(false)
        })
        .catch(() => {
          setLoading(false)
        })
    }
  }, [companyId, dispatch])

  const handleDelete = () => {
    setDeleting(true)
    dispatch(deleteCompany(companyId))
      .then(() => {
        setDeleting(false)
        navigate('/dashboard/companies')
      })
      .catch(() => {
        setDeleting(false)
      })
  }

  const handleEdit = () => {
    navigate(`/dashboard/companies/${companyId}/edit`)
  }

  const handleStatusToggle = () => {
    const newStatus = company.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE'
    dispatch(updateCompany(companyId, { status: newStatus }))
      .then(() => {
        setCompany({ ...company, status: newStatus })
      })
  }

  return (
    <Container>
      <Modal showModal={true}>
        <div className="company-details--container">
          <header>
            <h2>Company Details</h2>
            <button className="close--btn" onClick={() => navigate('/dashboard/companies')}>
              <FiX />
            </button>
          </header>

          {loading ? (
            <div className="loading--container">
              <Spinner size="50px" />
              <p>Loading company details...</p>
            </div>
          ) : company ? (
            <>
              <div className="tabs--container">
                <button
                  className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
                  onClick={() => setActiveTab('overview')}
                >
                  Overview
                </button>
                <button
                  className={`tab ${activeTab === 'business' ? 'active' : ''}`}
                  onClick={() => setActiveTab('business')}
                >
                  Business Info
                </button>
                <button
                  className={`tab ${activeTab === 'actions' ? 'active' : ''}`}
                  onClick={() => setActiveTab('actions')}
                >
                  Actions
                </button>
              </div>

              <div className="details--content">
                {/* Overview Tab */}
                {activeTab === 'overview' && (
                  <div className="tab-content">
                    <div className="detail--row">
                      <label>Company Name</label>
                      <p>{company.company_name}</p>
                    </div>
                    <div className="detail--row">
                      <label>Email</label>
                      <p>{company.company_email}</p>
                    </div>
                    <div className="detail--row">
                      <label>Contact Person</label>
                      <p>{company.contact_person}</p>
                    </div>
                    <div className="detail--row">
                      <label>Contact Phone</label>
                      <p>{company.contact_phone}</p>
                    </div>
                    <div className="detail--row">
                      <label>Status</label>
                      <p className={`status ${company.status?.toLowerCase()}`}>
                        {company.status || 'ACTIVE'}
                      </p>
                    </div>
                  </div>
                )}

                {/* Business Info Tab */}
                {activeTab === 'business' && (
                  <div className="tab-content">
                    <div className="detail--row">
                      <label>Business Address</label>
                      <p>{company.business_address || 'Not provided'}</p>
                    </div>
                    {company.city && (
                      <div className="detail--row">
                        <label>City</label>
                        <p>{company.city}</p>
                      </div>
                    )}
                    {company.state && (
                      <div className="detail--row">
                        <label>State</label>
                        <p>{company.state}</p>
                      </div>
                    )}
                    {company.country && (
                      <div className="detail--row">
                        <label>Country</label>
                        <p>{company.country}</p>
                      </div>
                    )}
                    {company.business_type && (
                      <div className="detail--row">
                        <label>Business Type</label>
                        <p>{company.business_type}</p>
                      </div>
                    )}
                    <div className="detail--row">
                      <label>Created</label>
                      <p>{new Date(company.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="detail--row">
                      <label>Last Updated</label>
                      <p>{new Date(company.updated_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                )}

                {/* Actions Tab */}
                {activeTab === 'actions' && (
                  <div className="tab-content actions-tab">
                    <div className="action--card">
                      <div className="action--info">
                        <h4>Edit Company</h4>
                        <p>Update company information and contact details</p>
                      </div>
                      <Button onClick={handleEdit} className="btn-edit">
                        <FiEdit2 /> Edit
                      </Button>
                    </div>

                    <div className="action--card">
                      <div className="action--info">
                        <h4>Toggle Status</h4>
                        <p>
                          Current status: <strong>{company.status}</strong>
                        </p>
                      </div>
                      <Button onClick={handleStatusToggle} className="btn-status">
                        {company.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
                      </Button>
                    </div>

                    <div className="action--card danger">
                      <div className="action--info">
                        <h4>Delete Company</h4>
                        <p>Permanently remove this company from the system</p>
                      </div>
                      <Button
                        onClick={() => setShowDeleteConfirm(true)}
                        className="btn-delete"
                      >
                        <FiTrash2 /> Delete
                      </Button>
                    </div>

                    {showDeleteConfirm && (
                      <div className="delete-confirm">
                        <p>Are you sure you want to delete <strong>{company.company_name}</strong>?</p>
                        <p className="warning">This action cannot be undone.</p>
                        <div className="confirm-actions">
                          <Button
                            onClick={() => setShowDeleteConfirm(false)}
                            className="btn-cancel"
                          >
                            Cancel
                          </Button>
                          <Button
                            onClick={handleDelete}
                            loading={deleting}
                            className="btn-confirm-delete"
                          >
                            {deleting ? 'Deleting...' : 'Yes, Delete'}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="actions">
                <Button onClick={() => navigate('/dashboard/companies')}>
                  Close
                </Button>
              </div>
            </>
          ) : (
            <div className="error--message">
              <p>Company not found</p>
            </div>
          )}
        </div>
      </Modal>
    </Container>
  )
}

export default CompanyDetails
